import {
  getBrowserLanguageOrEnglish,
  getCultureByLanguage,
  getLanguageSelectionFromLocalStorage,
} from '@jpi-cloud-web-pro/jpi-cloud-shared/utils/getCultureByLanguage';
import { cultureDefinitions } from './config';

import { generateD3TimeLocales } from './utils';

const supportedLanguageLoaders = {
  cs: () => import(/* webpackChunkName: "cs" */ './supported-languages/cs.json'),
  da: () => import(/* webpackChunkName: "da" */ './supported-languages/da.json'),
  de: () => import(/* webpackChunkName: "de" */ './supported-languages/de.json'),
  en: () => import(/* webpackChunkName: "en" */ './supported-languages/en.json'),
  es: () => import(/* webpackChunkName: "es" */ './supported-languages/es.json'),
  et: () => import(/* webpackChunkName: "et" */ './supported-languages/et.json'),
  fi: () => import(/* webpackChunkName: "fi" */ './supported-languages/fi.json'),
  fr: () => import(/* webpackChunkName: "fr" */ './supported-languages/fr.json'),
  hu: () => import(/* webpackChunkName: "hu" */ './supported-languages/hu.json'),
  it: () => import(/* webpackChunkName: "it" */ './supported-languages/it.json'),
  lt: () => import(/* webpackChunkName: "lt" */ './supported-languages/lt.json'),
  lv: () => import(/* webpackChunkName: "lv" */ './supported-languages/lv.json'),
  nb: () => import(/* webpackChunkName: "nb" */ './supported-languages/nb.json'),
  nl: () => import(/* webpackChunkName: "nl" */ './supported-languages/nl.json'),
  pl: () => import(/* webpackChunkName: "pl" */ './supported-languages/pl.json'),
  pt: () => import(/* webpackChunkName: "pt" */ './supported-languages/pt.json'),
  ro: () => import(/* webpackChunkName: "ro" */ './supported-languages/ro.json'),
  ru: () => import(/* webpackChunkName: "ru" */ './supported-languages/ru.json'),
  sk: () => import(/* webpackChunkName: "sk" */ './supported-languages/sk.json'),
  sl: () => import(/* webpackChunkName: "sl" */ './supported-languages/sl.json'),
  sv: () => import(/* webpackChunkName: "sv" */ './supported-languages/sv.json'),
  tr: () => import(/* webpackChunkName: "tr" */ './supported-languages/tr.json'),
};

const d3TimeLocaleLoaders = {
  cs: () => import(/* webpackChunkName: "d3-cs" */ 'd3-time-format/locale/cs-CZ.json'),
  da: () => import(/* webpackChunkName: "d3-da" */ 'd3-time-format/locale/da-DK.json'),
  de: () => import(/* webpackChunkName: "d3-de" */ 'd3-time-format/locale/de-DE.json'),
  en: () => import(/* webpackChunkName: "d3-en" */ 'd3-time-format/locale/en-US.json'),
  es: () => import(/* webpackChunkName: "d3-es" */ 'd3-time-format/locale/es-ES.json'),
  fi: () => import(/* webpackChunkName: "d3-fi" */ 'd3-time-format/locale/fi-FI.json'),
  fr: () => import(/* webpackChunkName: "d3-fr" */ 'd3-time-format/locale/fr-FR.json'),
  hu: () => import(/* webpackChunkName: "d3-hu" */ 'd3-time-format/locale/hu-HU.json'),
  it: () => import(/* webpackChunkName: "d3-it" */ 'd3-time-format/locale/it-IT.json'),
  nb: () => import(/* webpackChunkName: "d3-nb" */ 'd3-time-format/locale/nb-NO.json'),
  nl: () => import(/* webpackChunkName: "d3-nl" */ 'd3-time-format/locale/nl-NL.json'),
  pl: () => import(/* webpackChunkName: "d3-pl" */ 'd3-time-format/locale/pl-PL.json'),
  pt: () => import(/* webpackChunkName: "d3-pt" */ 'd3-time-format/locale/pt-BR.json'),
  ru: () => import(/* webpackChunkName: "d3-ru" */ 'd3-time-format/locale/ru-RU.json'),
  sv: () => import(/* webpackChunkName: "d3-sv" */ 'd3-time-format/locale/sv-SE.json'),
  tr: () => import(/* webpackChunkName: "d3-tr" */ 'd3-time-format/locale/tr-TR.json'),
};

const d3TimeLocaleGenerators = {
  et: generateD3TimeLocales,
  ro: generateD3TimeLocales,
  sk: generateD3TimeLocales,
  sl: generateD3TimeLocales,
  lt: generateD3TimeLocales,
  lv: generateD3TimeLocales,
};

const toJsonData = async importFn => {
  const module = await importFn();
  return module.default || module;
};

const getCultureKey = language => {
  const matchedCulture = getCultureByLanguage(language, cultures);
  return matchedCulture.language;
};

const loadedCultures = {};
const loadedBaseMessages = {};

export const cultures = Object.entries(cultureDefinitions).reduce((acc, [language, definition]) => {
  acc[language] = {
    language,
    ...definition,
    messages: {},
  };

  return acc;
}, {});

const loadBaseMessages = async language => {
  if (loadedBaseMessages[language]) {
    return loadedBaseMessages[language];
  }

  const languageLoader = supportedLanguageLoaders[language] || supportedLanguageLoaders.en;
  loadedBaseMessages[language] = await toJsonData(languageLoader);
  return loadedBaseMessages[language];
};

const loadD3TimeLocale = async (language, messages) => {
  if (d3TimeLocaleLoaders[language]) {
    return toJsonData(d3TimeLocaleLoaders[language]);
  }

  if (d3TimeLocaleGenerators[language]) {
    return d3TimeLocaleGenerators[language]({ messages });
  }

  return toJsonData(d3TimeLocaleLoaders.en);
};

export const loadCulture = async language => {
  const cultureKey = getCultureKey(language);

  if (loadedCultures[cultureKey]) {
    return loadedCultures[cultureKey];
  }

  const messages = await loadBaseMessages(cultureKey);

  const mergedMessages = {
    ...messages,
  };

  const d3TimeLocale = await loadD3TimeLocale(cultureKey, messages);

  loadedCultures[cultureKey] = {
    ...cultures[cultureKey],
    messages: mergedMessages,
    d3TimeLocale,
  };

  return loadedCultures[cultureKey];
};

export const loadCultureByLanguage = async language => loadCulture(getCultureKey(language));

export const getLanguageFromLocalStorage = () => cultures[getLanguageSelectionFromLocalStorage()];

export const getDefaultLanguage = () => getLanguageFromLocalStorage() || getBrowserLanguageOrEnglish(cultures);
